import { employeesApiBaseUrl } from '../../shared/env';
import { createJsonClient } from '../../shared/httpClient';
import type { EmployeeQuestion, QuestionFieldKey } from './questions/questionFields';
import type { EmployeeOnboardingGateway } from './EmployeeOnboardingGateway';
import type {
  SelfStateWithholdingElectionsResponse,
  SelfSubmitStateWithholdingElectionInput,
  StateWithholdingCatalogResponse,
  StateWithholdingSubmissionDetail,
} from '../people/types';
import type {
  AcceptInvitationInput,
  AcceptInvitationResponse,
  EmergencyContactDto,
  EmployeeSetupResponse,
  EmployeeSubmission,
  InvitationCheckResponse,
  SaveAddressesInput,
  SaveFederalW4Input,
  SavePersonalInput,
  SaveStateTaxInput,
  TaxSetupResponse,
} from './types';
import { mapTaxSetupStatus } from '../tax-settings/wire';

export class HttpEmployeeOnboardingGateway implements EmployeeOnboardingGateway {
  // Identity is same-origin with no service prefix (like the other Identity gateways); Employees goes through its prefix.
  private readonly identityRequest = createJsonClient('');
  private readonly employeesRequest = createJsonClient(employeesApiBaseUrl);

  checkInvitation(token: string): Promise<InvitationCheckResponse> {
    return this.identityRequest('/api/employee-invitations/check', { method: 'POST', body: JSON.stringify({ token }) });
  }

  acceptInvitation(input: AcceptInvitationInput): Promise<AcceptInvitationResponse> {
    return this.identityRequest('/api/employee-invitations/accept', {
      method: 'POST',
      body: JSON.stringify({ token: input.token, password: input.password, confirmPassword: input.confirmPassword }),
    });
  }

  async listMyQuestions(): Promise<EmployeeQuestion[]> {
    const result = await this.identityRequest<{ items: EmployeeQuestion[] }>('/api/me/employee-questions');
    return result.items;
  }

  askQuestion(fieldKey: QuestionFieldKey, message: string): Promise<EmployeeQuestion> {
    return this.identityRequest('/api/me/employee-questions', { method: 'POST', body: JSON.stringify({ fieldKey, message }) });
  }

  getSetup(): Promise<EmployeeSetupResponse> {
    return this.employeesRequest('/api/me/employee/setup');
  }

  async savePersonal(input: SavePersonalInput): Promise<void> {
    // Exactly the five allow-listed fields, never the whole profile.
    const body: SavePersonalInput = {
      firstName: input.firstName,
      lastName: input.lastName,
      phone: input.phone,
      gender: input.gender,
      dateOfBirth: input.dateOfBirth,
    };
    await this.employeesRequest('/api/me/employee/personal', { method: 'PUT', body: JSON.stringify(body) });
  }

  async saveEmergencyContact(input: EmergencyContactDto): Promise<void> {
    const body: EmergencyContactDto = { fullName: input.fullName, phone: input.phone, relation: input.relation };
    await this.employeesRequest('/api/me/employee/emergency-contact', { method: 'PUT', body: JSON.stringify(body) });
  }

  async saveAddresses(input: SaveAddressesInput): Promise<void> {
    const body: SaveAddressesInput = {
      residence: input.residence,
      mailingSameAsResidence: input.mailingSameAsResidence,
      ...(input.mailingSameAsResidence || !input.mailing ? {} : { mailing: input.mailing }),
      ...(input.residenceIsPrimaryWorkLocation === undefined ? {} : { residenceIsPrimaryWorkLocation: input.residenceIsPrimaryWorkLocation }),
    };
    await this.employeesRequest('/api/me/employee/addresses', { method: 'PUT', body: JSON.stringify(body) });
  }

  async acknowledgeWorkLocation(): Promise<void> {
    await this.employeesRequest('/api/me/employee/work-location-acknowledgement', {
      method: 'PUT',
      body: JSON.stringify({ acknowledged: true }),
    });
  }

  async saveCheckPayMethod(): Promise<void> {
    await this.employeesRequest('/api/me/employee/pay-method', { method: 'PUT', body: JSON.stringify({ method: 'Check' }) });
  }

  async getTaxes(): Promise<TaxSetupResponse> {
    const raw = await this.employeesRequest<TaxSetupResponse & { taxSetup?: unknown }>('/api/me/employee/taxes');
    // The tax setup status is read tolerantly: a member this build does not know is ignored, and an absent or unreadable one is null (never Complete).
    return { ...raw, taxSetup: mapTaxSetupStatus(raw.taxSetup) };
  }

  async saveFederalW4(input: SaveFederalW4Input): Promise<void> {
    // Exactly the allow-listed W-4 answers on the CURRENT form; never a work state, residence state, SUI or approval.
    const body: SaveFederalW4Input = {
      formVersion: input.formVersion,
      filingStatus: input.filingStatus,
      multipleJobs: input.multipleJobs,
      dependentsAmount: input.dependentsAmount,
      otherIncome: input.otherIncome,
      deductions: input.deductions,
      extraWithholding: input.extraWithholding,
      ...(input.mostRecentWithholdingAmount === undefined ? {} : { mostRecentWithholdingAmount: input.mostRecentWithholdingAmount }),
    };
    await this.employeesRequest('/api/me/employee/taxes/federal', { method: 'PUT', body: JSON.stringify(body) });
  }

  async saveStateTax(input: SaveStateTaxInput): Promise<void> {
    const body: SaveStateTaxInput = {
      stateCode: input.stateCode,
      ...(input.filingStatus === undefined ? {} : { filingStatus: input.filingStatus }),
      ...(input.allowances === undefined ? {} : { allowances: input.allowances }),
      ...(input.mostRecentWithholdingAmount === undefined ? {} : { mostRecentWithholdingAmount: input.mostRecentWithholdingAmount }),
      ...(input.additionalWithholdingState === undefined ? {} : { additionalWithholdingState: input.additionalWithholdingState }),
      ...(input.additionalWithholdingValue === undefined ? {} : { additionalWithholdingValue: input.additionalWithholdingValue }),
      ...(input.additionalWithholdingType === undefined ? {} : { additionalWithholdingType: input.additionalWithholdingType }),
    };
    await this.employeesRequest('/api/me/employee/taxes/state', { method: 'PUT', body: JSON.stringify(body) });
  }

  async clearTaxDraft(section: 'federal' | 'state'): Promise<void> {
    await this.employeesRequest(`/api/me/employee/taxes/draft?section=${section}`, { method: 'DELETE' });
  }

  async submitReciprocityCertificate(file: File): Promise<void> {
    const form = new FormData();
    form.append('file', file);
    await this.employeesRequest('/api/me/employee/taxes/reciprocity-certificate', { method: 'POST', body: form });
  }

  getSubmission(): Promise<EmployeeSubmission> {
    return this.employeesRequest('/api/me/employee/submission');
  }

  submitOnboarding(): Promise<EmployeeSubmission> {
    return this.employeesRequest('/api/me/employee/submit', { method: 'POST' });
  }

  async withdrawReciprocityRequest(): Promise<void> {
    await this.employeesRequest('/api/me/employee/taxes/reciprocity-request', { method: 'DELETE' });
  }

  getStateWithholdingCatalog(): Promise<StateWithholdingCatalogResponse> {
    return this.employeesRequest('/api/state-withholding-certificate-catalog');
  }

  getMyStateWithholdingElections(): Promise<SelfStateWithholdingElectionsResponse> {
    return this.employeesRequest('/api/me/employee/taxes/state-elections');
  }

  submitMyStateWithholdingElection(input: SelfSubmitStateWithholdingElectionInput): Promise<StateWithholdingSubmissionDetail> {
    // Only the self-service members: the server refuses an unknown one (a backdate reason, for example).
    const { stateCode, effectiveFrom, filingStatusKey, exemptions, additionalWithholding, isExempt, electedRate, exemptionAmount, dependents, lowerRateElection } = input;
    const body = { stateCode, effectiveFrom, filingStatusKey, exemptions, additionalWithholding, isExempt, electedRate, exemptionAmount, dependents, lowerRateElection };
    return this.employeesRequest('/api/me/employee/taxes/state-elections', { method: 'POST', body: JSON.stringify(body) });
  }

  withdrawMyStateWithholdingElection(stateCode: string): Promise<StateWithholdingSubmissionDetail> {
    return this.employeesRequest(`/api/me/employee/taxes/state-elections?state=${encodeURIComponent(stateCode)}`, { method: 'DELETE' });
  }
}
